/**
 * NotificationBell Component
 * Displays notification bell icon with unread badge and dropdown
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faCheck,
    faCheckDouble,
    faTimes,
    faChartLine,
    faExclamationTriangle,
    faInfoCircle,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from '../../hooks/useNotifications';
import type { Notification } from '../../services/api/notifications';
import { getNotificationNavigationTarget } from '../../utils/notificationNavigation';
import { getNotificationDisplayMessage } from '../../utils/notificationText';

interface NotificationBellProps {
    className?: string;
    /** Position dropdown above or below the bell */
    dropdownPosition?: 'up' | 'down';
    /** Show "Notifications" label beside the bell */
    showLabel?: boolean;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
    className = '',
    dropdownPosition = 'down',
    showLabel = false
}) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const {
        notifications,
        unreadCount,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        dismiss,
        refresh
    } = useNotifications({
        autoFetch: true,
        pollInterval: 30000 // Poll every 30 seconds
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleBellClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            refresh();
        }
    };

    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read
        if (!notification.read) {
            await markAsRead(notification.id);
        }

        // Navigate to action URL if present
        const target = getNotificationNavigationTarget(notification.actionUrl);
        if (target) {
            setIsOpen(false);
            navigate(target);
        }
    };

    const handleDismiss = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await dismiss(id);
    };

    const getNotificationIcon = (type: Notification['type']) => {
        switch (type) {
            case 'optimization_complete':
            case 'wfa_complete':
            case 'backtest_complete':
                return { icon: faChartLine, color: 'text-green-500' };
            case 'optimization_failed':
            case 'wfa_failed':
            case 'backtest_failed':
                return { icon: faExclamationTriangle, color: 'text-red-500' };
            case 'system':
                return { icon: faInfoCircle, color: 'text-blue-500' };
            default:
                return { icon: faInfoCircle, color: 'text-gray-500' };
        }
    };

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={handleBellClick}
                className={`relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-md ${showLabel ? 'w-full flex items-center justify-start' : ''
                    }`}
                aria-label="Notifications"
            >
                <FontAwesomeIcon icon={faBell} className="text-lg w-4 h-4" />

                {showLabel && (
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                        Notifications
                    </span>
                )}

                {/* Unread Badge */}
                {unreadCount > 0 && (
                    <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full px-1 ${showLabel ? 'ml-auto' : 'absolute -top-1 -right-1'
                        }`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className={`absolute w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-[70vh] overflow-hidden flex flex-col ${dropdownPosition === 'up'
                    ? 'bottom-full mb-2 left-0'
                    : 'top-full mt-2 right-0'
                    }`}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            Notifications
                        </h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={() => markAllAsRead()}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                            >
                                <FontAwesomeIcon icon={faCheckDouble} className="text-xs" />
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Notification List */}
                    <div className="overflow-y-auto flex-1">
                        {loading && notifications.length === 0 ? (
                            <div className="flex items-center justify-center py-8">
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-xl" />
                            </div>
                        ) : error ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl mb-2 opacity-50 text-amber-500" />
                                <p className="text-sm">Unable to load notifications</p>
                                <button
                                    onClick={() => refresh()}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2"
                                >
                                    Try again
                                </button>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <FontAwesomeIcon icon={faBell} className="text-3xl mb-2 opacity-50" />
                                <p className="text-sm">No notifications</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {notifications.slice(0, 10).map((notification) => {
                                    const { icon, color } = getNotificationIcon(notification.type);

                                    return (
                                        <div
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                                                }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                {/* Icon */}
                                                <div className={`mt-0.5 ${color}`}>
                                                    <FontAwesomeIcon icon={icon} />
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className={`text-sm font-medium truncate ${!notification.read
                                                            ? 'text-gray-900 dark:text-white'
                                                            : 'text-gray-700 dark:text-gray-300'
                                                            }`}>
                                                            {notification.title}
                                                        </p>

                                                        {/* Dismiss button */}
                                                        <button
                                                            onClick={(e) => handleDismiss(e, notification.id)}
                                                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                                        </button>
                                                    </div>

                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                                                        {getNotificationDisplayMessage(notification)}
                                                    </p>

                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-xs text-gray-400 dark:text-gray-500">
                                                            {formatTimeAgo(notification.createdAt)}
                                                        </span>
                                                        {!notification.read && (
                                                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 10 && (
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/notifications');
                                }}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                View all notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
